const DELAY_THRESHOLD_MINUTES = 15;
const CAN_FINISH_THRESHOLD_MINUTES = 30;

export const UI_STATUS = Object.freeze({
  NOT_STARTED_YET: "not_started_yet",
  NEED_REPLY: "need_reply",
  REPLIED: "replied",
  CAN_FINISH: "can_finish"
});

export const FINISH_BLOCKED_STATUSES = Object.freeze([
  UI_STATUS.NEED_REPLY,
  UI_STATUS.REPLIED,
  UI_STATUS.CAN_FINISH
]);

const NON_ATTENTION_STATUSES = [UI_STATUS.REPLIED, UI_STATUS.CAN_FINISH];

export function decorateAssignedSheet(sheet, { currentTimeMs, serverTimeOffsetMs }) {
  const localProgress = resolveProgress(sheet, { currentTimeMs, serverTimeOffsetMs });
  const progress = mergeProgress(sheet.progress, localProgress);
  const localDelayed = resolveDelayed(sheet, progress, { currentTimeMs, serverTimeOffsetMs });
  const isDelayed = sheet.isDelayed === true || localDelayed;
  const elapsedMinutes = resolveElapsedMinutes(sheet, progress, { currentTimeMs, serverTimeOffsetMs });
  const groupKey =
    sheet.groupKey != null ? sheet.groupKey : (isNonAttentionStatus(progress) ? "non_attention" : "attention");

  return {
    ...sheet,
    progress,
    isDelayed,
    elapsedMinutes,
    groupKey,
  };
}

export function compareAssignedSheets(left, right) {
  const leftPriority = left.groupKey == "attention" ? 0 : 1;
  const rightPriority = right.groupKey == "attention" ? 0 : 1;

  if (leftPriority != rightPriority) return leftPriority - rightPriority;

  const leftBaseTime = sortBaseTimeMs(left);
  const rightBaseTime = sortBaseTimeMs(right);
  if (leftBaseTime != rightBaseTime) return leftBaseTime - rightBaseTime;

  return left.id - right.id;
}

export function isSheetFinishBlocked(sheet) {
  return FINISH_BLOCKED_STATUSES.includes(sheet.progress);
}

export function statusLabel(sheet) {
  const progress = sheet.progress;

  if (progress == UI_STATUS.NOT_STARTED_YET) return "未対応";
  if (progress == UI_STATUS.NEED_REPLY) return "要返信";
  if (progress == UI_STATUS.REPLIED) return "返信済み";
  if (progress == UI_STATUS.CAN_FINISH) return "終了可能";

  return sheet.statusI18n;
}

export function elapsedTimeLabel(sheet) {
  const elapsedMinutes = sheet.elapsedMinutes != null ? sheet.elapsedMinutes : 0;

  if (sheet.elapsedFrom == "doctor") {
    return "あなたの発言から" + elapsedMinutes + "分";
  }

  return "ユーザーの発言から" + elapsedMinutes + "分";
}

export function toTimeMs(value) {
  if (value == null) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;

  return time;
}

function resolveProgress(sheet, { currentTimeMs, serverTimeOffsetMs }) {
  if (sheet.status == 1) return UI_STATUS.NOT_STARTED_YET;

  if (sheet.status == 2) {
    if (sheet.lastSender == "user") return UI_STATUS.NEED_REPLY;

    const doctorLastSentAt = toTimeMs(sheet.doctorLastSentAt || sheet.lastSentAtIso8601);
    if (elapsedMinutes(doctorLastSentAt, { currentTimeMs, serverTimeOffsetMs }) >= CAN_FINISH_THRESHOLD_MINUTES) {
      return UI_STATUS.CAN_FINISH;
    }
    return UI_STATUS.REPLIED;
  }

  return sheet.progress || UI_STATUS.REPLIED;
}

function resolveDelayed(sheet, progress, { currentTimeMs, serverTimeOffsetMs }) {
  if (![UI_STATUS.NOT_STARTED_YET, UI_STATUS.NEED_REPLY].includes(progress)) return false;

  const userWaitStartedAt = resolveUserWaitStartedAtMs(sheet);
  return elapsedMinutes(userWaitStartedAt, { currentTimeMs, serverTimeOffsetMs }) >= DELAY_THRESHOLD_MINUTES;
}

function resolveElapsedMinutes(sheet, progress, { currentTimeMs, serverTimeOffsetMs }) {
  let baseTimeMs = null;
  if (isNonAttentionStatus(progress)) {
    baseTimeMs = toTimeMs(sheet.doctorLastSentAt || sheet.lastSentAtIso8601);
  } else {
    baseTimeMs = resolveUserWaitStartedAtMs(sheet);
  }

  return elapsedMinutes(baseTimeMs, { currentTimeMs, serverTimeOffsetMs });
}

function elapsedMinutes(baseTimeMs, { currentTimeMs, serverTimeOffsetMs }) {
  if (baseTimeMs == null) return 0;

  const nowMs = currentTimeMs + serverTimeOffsetMs;
  return Math.max(Math.floor((nowMs - baseTimeMs) / 60000), 0);
}

function isNonAttentionStatus(progress) {
  return NON_ATTENTION_STATUSES.includes(progress);
}

function sortBaseTimeMs(sheet) {
  if (sheet.groupKey == "attention") {
    return resolveUserWaitStartedAtMs(sheet) || 0;
  }
  return toTimeMs(sheet.doctorLastSentAt || sheet.lastSentAtIso8601) || 0;
}

function resolveUserWaitStartedAtMs(sheet) {
  return toTimeMs(sheet.userWaitStartedAt || sheet.userLastSentAt || sheet.lastSentAtIso8601);
}

function mergeProgress(serverProgress, localProgress) {
  if (serverProgress == null) return localProgress;

  if (serverProgress == UI_STATUS.REPLIED && localProgress == UI_STATUS.CAN_FINISH) {
    return UI_STATUS.CAN_FINISH;
  }

  return serverProgress;
}
